import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  useTheme,
} from '@mui/material'
import { useContext, useEffect, useState } from 'react'
import Tooltip from '@mui/material/Tooltip'
import ReplyIcon from '@mui/icons-material/Reply'
import { Alert, useApplicationSuiteContext } from '../../../contexts/ApplicationSuiteProvider'
import generateShareLink from '../../../common/utils/generateShareLink'
import ShareDialog from '../../builder/components/sidebar/patternInput/ShareDialog'
import { useAlertContext } from '../contexts/AlertContext'
import { setNestedAlertSymbol } from '../../../common/utils/setNestedAlertSymbol'
import { updateDoc } from 'firebase/firestore'
import { doc } from 'firebase/firestore'
import { firebase_firestore } from '../../../common/firebaseConfig'
import { useCallableRef } from '../../../api/useCallableRef'
import { SnackbarHelperContext } from '../../../contexts/SnackbarHelperContext'
import dayjs from 'dayjs'
import EditIcon from '@mui/icons-material/Edit'
import SaveIcon from '@mui/icons-material/Save'
import DeleteIcon from '@mui/icons-material/Delete'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material'
import { getClientTimeZone } from '../../../common/timezones'
import DuplicateAlertDialog from './DuplicateAlertDialog'

function AlertSettingsActionBar({ inEditMode, alert }: { inEditMode?: boolean; alert: Alert }) {
  const theme = useTheme()

  const { setSnackbarSettings } = useContext(SnackbarHelperContext)

  const { usermeta, canCreateAlerts } = useApplicationSuiteContext()

  const {
    selectedAlert,
    closeSidePanel,
    enterEditMode,
    setSelectedAlert,
    exitEditMode,
    openSidePanel,
  } = useAlertContext()

  const [deleteAlertCallable] = useCallableRef('deletealert')

  const [isShareDialogOpen, setIsShareDialogOpen] = useState(false)
  const [shareable, setShareable] = useState<any>(null)
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const [isDuplicateDialogOpen, setIsDuplicateDialogOpen] = useState(false)
  const [alertToUpdate, setAlertToUpdate] = useState(alert)

  useEffect(() => {
    setAlertToUpdate(alert)
  }, [alert])

  if (!selectedAlert) return null

  async function shareBacktest() {
    const shareable = await generateShareLink(alert.id, `tradingPlans/${alert.id}`, 'ALERT', {
      name: alert.name,
      createdBy: { gcid: usermeta?.gcid, email: usermeta?.email },
    })
    setShareable(shareable)
    setIsShareDialogOpen(true)
  }

  const handleEditClick = () => {
    if (selectedAlert) {
      enterEditMode(selectedAlert.id)
    }
  }

  const handleSave = async () => {
    if (!alertToUpdate.id) return

    try {
      // Ensure symbol is propagated to all nested pattern conditions
      if (alertToUpdate.symbol && alertToUpdate.pattern) {
        setNestedAlertSymbol(alertToUpdate.pattern, alertToUpdate.symbol)
      }

      if (alertToUpdate.timezone === 'UTC') {
        // Get the user's timezone from the browser
        const userTimezone = getClientTimeZone()
        alertToUpdate.timezone = userTimezone

        // update timeRanges to be in the user's timezone
        alertToUpdate.timeRanges = alertToUpdate.timeRanges.map((timeRange) => {
          // Parse the UTC time strings and convert to user's timezone
          const startTime = dayjs.tz(timeRange.startTimeRange, 'HH:mm', 'UTC').tz(userTimezone)
          const endTime = dayjs.tz(timeRange.endTimeRange, 'HH:mm', 'UTC').tz(userTimezone)

          return {
            ...timeRange,
            startTimeRange: startTime.format('HH:mm'),
            endTimeRange: endTime.format('HH:mm'),
          }
        })
      }

      await updateDoc(doc(firebase_firestore, `tradingPlans/${alert.id}`), alertToUpdate)
      setSelectedAlert(alertToUpdate)
      setSnackbarSettings({
        severity: 'success',
        message: `Alert updated!`,
        autoHideDuration: 3000,
        anchorOrigin: { vertical: 'bottom', horizontal: 'center' },
      })
    } catch (err) {
      console.error('Error saving alert:', err)
      setSnackbarSettings({
        severity: 'error',
        message: `Alert failed to update!`,
        autoHideDuration: 3000,
        anchorOrigin: { vertical: 'bottom', horizontal: 'center' },
      })
    }
  }

  const handleDeleteClick = () => {
    setIsDeleteDialogOpen(true)
  }

  const handleCancelDelete = () => {
    setIsDeleteDialogOpen(false)
  }

  const handleConfirmDelete = async () => {
    if (selectedAlert) {
      try {
        await deleteAlertCallable({ alertId: selectedAlert.id })
        // Close the dialog and side panel
        setIsDeleteDialogOpen(false)
        closeSidePanel()
        setSnackbarSettings({
          severity: 'success',
          message: `Alert deleted!`,
          autoHideDuration: 3000,
          anchorOrigin: { vertical: 'bottom', horizontal: 'center' },
        })
      } catch (error) {
        console.error('Error deleting alert:', error)
        setSnackbarSettings({
          severity: 'error',
          message: `Alert failed to delete!`,
          autoHideDuration: 3000,
          anchorOrigin: { vertical: 'bottom', horizontal: 'center' },
        })
      }
    }
  }

  const handleBack = () => {
    if (selectedAlert) {
      exitEditMode()
      setTimeout(() => {
        openSidePanel(alert)
      }, 50)
    } else {
      exitEditMode()
    }
  }

  return (
    <>
      <Box
        sx={{
          position: 'sticky',
          bottom: 0,
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          paddingTop: '12px',
          paddingBottom: '12px',
          paddingLeft: '16px',
          paddingRight: '16px',
          backgroundColor: theme.palette.background.custom4,
          boxShadow: '0 -2px 5px rgba(0,0,0,0.1)',
          boxSizing: 'border-box',
          zIndex: 1000,
        }}
      >
        <Box
          sx={{ display: 'flex', justifyContent: 'space-between' }}
          data-tour-id="alert-side-panel-actions"
        >
          <Box sx={{ display: 'flex' }}>
            <Tooltip title="Delete alert" placement="top">
              <IconButton onClick={handleDeleteClick} color="error">
                <DeleteIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Share alert" placement="top">
              <IconButton onClick={shareBacktest}>
                <ReplyIcon sx={{ transform: 'scaleX(-1)' }} />
              </IconButton>
            </Tooltip>
            {canCreateAlerts && (
              <Tooltip title="Duplicate alert" placement="top">
                <IconButton onClick={() => setIsDuplicateDialogOpen(true)}>
                  <ContentCopyIcon />
                </IconButton>
              </Tooltip>
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {inEditMode ? (
              <Button variant="outlined" onClick={handleBack} startIcon={<ArrowBackIcon />}>
                Back
              </Button>
            ) : null}

            {!inEditMode ? (
              <Button
                variant="contained"
                color="primary"
                endIcon={<EditIcon />}
                onClick={handleEditClick}
                disabled={!selectedAlert}
                data-tour-id="alert-side-panel-edit-button"
              >
                Edit
              </Button>
            ) : (
              <Button variant="contained" endIcon={<SaveIcon />} onClick={handleSave}>
                Save Changes
              </Button>
            )}
          </Box>
        </Box>
      </Box>
      <ShareDialog
        open={!!isShareDialogOpen}
        title={`Share this alert`}
        onClose={() => setIsShareDialogOpen(false)}
        shareable={shareable}
      />
      {/* Mounted conditionally so the dialog remounts each time and rebuilds a
          fresh draft from the current source alert via its lazy initializer. */}
      {isDuplicateDialogOpen && (
        <DuplicateAlertDialog
          open={isDuplicateDialogOpen}
          onClose={() => setIsDuplicateDialogOpen(false)}
          sourceAlert={alert}
        />
      )}
      {/* Confirmation Dialog */}
      <Dialog
        open={isDeleteDialogOpen}
        onClose={handleCancelDelete}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">{'Delete Alert?'}</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to delete this alert? This action cannot be undone and will delete
            the alert and all associated data.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete} variant="contained">
            Cancel
          </Button>
          <Button onClick={handleConfirmDelete} variant="outlined" color="error" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default AlertSettingsActionBar
